import React, { useCallback, useState, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useReactFlow,
  ReactFlowProvider,
  applyNodeChanges,
  type NodeTypes,
  type Node,
  type OnNodesChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import OrgChartNode from './OrgChartNode';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgChartData } from './useOrgChartData';
import { useOrgChartLayout } from './useOrgChartLayout';
import { useOrgChartDragDrop } from './useOrgChartDragDrop';
import type { TreeNode } from './orgChartUtils';

import { tokens } from '@design';
const nodeTypes: NodeTypes = { orgUnit: OrgChartNode };

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 1.5;
const DEFAULT_ZOOM = 0.8;

interface OrgChartFlowProps {
  organizationId?: number;
  organizationName?: string;
  onNodePress?: (node: TreeNode) => void;
  onMoveComplete?: () => void;
  isEditing?: boolean;
  onMoveCountChange?: (count: number) => void;
  confirmRef?: React.MutableRefObject<(() => void) | null>;
  undoRef?: React.MutableRefObject<(() => Promise<void>) | null>;
}

function OrgChartFlowInner({ organizationId, organizationName, onNodePress, onMoveComplete, isEditing = false, onMoveCountChange, confirmRef, undoRef }: OrgChartFlowProps) {
  const reactFlowInstance = useReactFlow();
  const { user } = useAuth();
  const canDrag = user?.profile?.user_type === 'org_employee' && isEditing;
  const [isReady, setIsReady] = useState(false);
  const [displayNodes, setDisplayNodes] = useState<Node[]>([]);

  const handleNodeClick = useCallback(
    (node: TreeNode) => {
      if (node.id !== -1 && onNodePress) {
        onNodePress(node);
      }
    },
    [onNodePress],
  );

  const { nodes, edges, loading, error, refreshTree } = useOrgChartData({
    organizationId,
    organizationName,
    onNodeClick: handleNodeClick,
  });

  const { layoutedNodes, layoutedEdges } = useOrgChartLayout(nodes, edges);

  useEffect(() => {
    setDisplayNodes(layoutedNodes);
  }, [layoutedNodes]);

  const onNodesChange: OnNodesChange = useCallback((changes) => {
    setDisplayNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const { onNodeDragStart, onNodeDrag, onNodeDragStop, dropTargetId, dropMode, confirmMoves, undoAllMoves } = useOrgChartDragDrop({
    edges: layoutedEdges,
    refreshTree,
    reactFlowInstance: isReady ? reactFlowInstance : null,
    onMoveComplete,
    onMoveCountChange,
  });

  // Expose confirm/undo to parent
  useEffect(() => {
    if (confirmRef) confirmRef.current = confirmMoves;
    if (undoRef) undoRef.current = undoAllMoves;
  }, [confirmRef, undoRef, confirmMoves, undoAllMoves]);

  // Apply drop-target highlighting
  const highlightedNodes = displayNodes.map((node) => {
    if (node.id !== dropTargetId) return { ...node, className: '' };
    return {
      ...node,
      className: dropMode === 'child' ? 'org-drop-child' : 'org-drop-sibling',
    };
  });

  const onInit = useCallback(() => {
    setIsReady(true);
  }, []);

  if (loading) {
    return (
      <div style={styles.stateContainer}>
        <div style={styles.spinner} />
        <p style={styles.stateText}>جاري تحميل الهيكل التنظيمي...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.stateContainer}>
        <p style={{ ...styles.stateText, color: tokens.colors.raw.red["500"] }}>{error}</p>
      </div>
    );
  }

  if (highlightedNodes.length === 0) {
    return (
      <div style={styles.stateContainer}>
        <p style={styles.stateText}>لا توجد وحدات تنظيمية</p>
      </div>
    );
  }

  return (
    <ReactFlow
      nodes={highlightedNodes}
      edges={layoutedEdges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      {...(canDrag ? {
        onNodeDragStart,
        onNodeDrag,
        onNodeDragStop,
      } : {})}
      onInit={onInit}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={MIN_ZOOM}
      maxZoom={MAX_ZOOM}
      defaultViewport={{ x: 0, y: 0, zoom: DEFAULT_ZOOM }}
      proOptions={{ hideAttribution: true }}
      nodesDraggable={canDrag}
      nodesConnectable={false}
      elementsSelectable={true}
      panOnDrag={canDrag ? [1, 2] : true}
      style={{ width: '100%', height: '100%' }}
    >
      <Background gap={16} size={1} color={tokens.colors.raw.gray["200"]} />
      <Controls showInteractive={false} style={{ bottom: 10, insetInlineStart: 10 }} />
    </ReactFlow>
  );
}

export default function OrgChartFlow(props: OrgChartFlowProps) {
  return (
    <ReactFlowProvider>
      <OrgChartFlowInner {...props} />
    </ReactFlowProvider>
  );
}

const styles: Record<string, React.CSSProperties> = {
  stateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
    padding: tokens.spacing.absolute["10"],
  },
  stateText: {
    fontFamily: 'JF-Flat-Regular, system-ui, sans-serif',
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["4"],
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid #E5E7EB',
    borderTopColor: '#0F9CD8',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};
